import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { Layout, TableSkeleton, PageHeader, StatCard, FilterBar, FilterField, EmptyState } from '../common';
import { Select, DatePicker, Alert, InputNumber, Button, Tooltip } from 'antd';
import {
    BadgeDollarSign,
    Package,
    PackageCheck,
    ReceiptText,
    Store,
    ClipboardCheck,
    Save,
    Info,
    Coins,
    ShieldAlert,
    TriangleAlert,
    RotateCw,
    ListChecks
} from 'lucide-react';
import dayjs from 'dayjs';
import { formatNumber } from '../../utils/format';
import DistributorPriceVisualsController from './distributorPrices.controller';
import ProductPriceExplorer from './elements/ProductPriceExplorer';

const { RangePicker } = DatePicker;

/** Labeled block for controls that shouldn't sit inside a <label> (range picker, input + button). */
const FieldBlock = ({ label, children, className = '' }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
        <span className="text-xs font-medium text-surface-500">{label}</span>
        {children}
    </div>
);

/** Card wrapper for empty / error states. */
const StateCard = ({ children }) => <div className="shelf-card">{children}</div>;

class DistributorPriceVisuals extends Inherit(DistributorPriceVisualsController, Component) {
    state = {
        loading: true,
        dataLoading: false,
        priceOptions: [],
        selectedType: null,
        selectedProductIds: [],
        focusedProductId: null,
        dateRange: [dayjs().subtract(30, 'day'), dayjs()],
        visuals: null,
        loadError: null,
        accessDenied: false,
        error: false,
        anomalyThreshold: 3,
        anomalyThresholdDraft: 3,
        savingThreshold: false,
        reviewingObservation: false,
    };

    renderFilters(vm) {
        const { priceOptions, selectedType, selectedProductIds, dateRange, visuals, anomalyThreshold, anomalyThresholdDraft, savingThreshold } =
            this.state;
        const { selectedOption, productFilterOptions, productsWithData, reportingStores } = vm;

        return (
            <FilterBar>
                <FilterField label="Lista / categoría" width={288}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        className="w-full"
                        placeholder="Lista de precios"
                        value={selectedType}
                        options={priceOptions}
                        onChange={(v) => this.onPriceTypeChange(v)}
                    />
                </FilterField>
                <FieldBlock label="Período">
                    <RangePicker value={dateRange} format="DD/MM/YYYY" onChange={(dates) => this.onDateRangeChange(dates)} />
                </FieldBlock>
                <FieldBlock
                    label={
                        <Tooltip title="Umbral de desviación (%) sobre el cual un precio se marca como sospechoso. Configurable.">
                            <span className="inline-flex items-center gap-1 cursor-help">
                                Umbral de anomalía (%) <Info className="text-surface-400" />
                            </span>
                        </Tooltip>
                    }>
                    <div className="flex items-center gap-2">
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.5}
                            value={anomalyThresholdDraft}
                            onChange={(v) => this.setAnomalyThresholdDraft(v)}
                            className="w-24"
                        />
                        <Button
                            icon={<Save />}
                            loading={savingThreshold}
                            disabled={Number(anomalyThresholdDraft) === Number(anomalyThreshold)}
                            onClick={() => this.saveAnomalyThreshold()}>
                            Guardar
                        </Button>
                    </div>
                </FieldBlock>
                {visuals && productFilterOptions.length > 0 && (
                    <FilterField label="Filtrar productos" width={320}>
                        <Select
                            mode="multiple"
                            showSearch
                            allowClear
                            maxTagCount="responsive"
                            optionFilterProp="label"
                            className="w-full"
                            placeholder="Todos los productos de la lista"
                            value={selectedProductIds}
                            options={productFilterOptions.map((p) => ({
                                value: p.value,
                                label: `${p.label}${p.observationCount ? ` (${p.observationCount})` : ''}`,
                            }))}
                            onChange={(ids) => this.setSelectedProductIds(ids)}
                        />
                    </FilterField>
                )}
                {selectedOption && visuals && (
                    <p className="basis-full m-0 pt-1 text-xs text-surface-500 tabular-nums">
                        {visuals.summary?.productCount || 0} productos en lista ·{' '}
                        {productsWithData} con precios en storechecks ·{' '}
                        {visuals.summary?.observationCount || 0} registros en{' '}
                        {reportingStores} pisos
                    </p>
                )}
            </FilterBar>
        );
    }

    renderVisuals(vm) {
        const { visuals, reviewingObservation, anomalyThreshold } = this.state;
        const { filteredProducts, focusedInFilter, productsWithData, observationTotal, reportingStores } = vm;

        return (
            <>
                {visuals.currencyInfo?.bcvRateLatest != null && (
                    <Alert
                        type="info"
                        showIcon
                        icon={<Coins />}
                        className="mb-4"
                        message="Precios normalizados a USD"
                        description={`Precios en bolívares se convierten con la tasa BCV del día de cada storecheck. Tasa BCV más reciente: Bs ${Number(visuals.currencyInfo.bcvRateLatest).toFixed(2)} / USD${visuals.currencyInfo.bcvRateDate ? ` (${dayjs(visuals.currencyInfo.bcvRateDate).format('DD/MM/YYYY')})` : ''}.${visuals.currencyInfo.skippedConversionCount ? ` ${visuals.currencyInfo.skippedConversionCount} registro(s) en Bs omitido(s) por falta de tasa.` : ''}`}
                    />
                )}

                <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 mb-4">
                    <StatCard label="Productos en lista" value={formatNumber(filteredProducts.length)} icon={<Package />} tone="brand" />
                    <StatCard label="Con precios en campo" value={formatNumber(productsWithData)} icon={<PackageCheck />} tone="success" />
                    <StatCard label="Registros de precio" value={formatNumber(observationTotal)} icon={<ReceiptText />} tone="info" />
                    <StatCard label="Pisos con registros" value={formatNumber(reportingStores)} icon={<Store />} tone="violet" />
                    <StatCard
                        label="Storechecks"
                        value={formatNumber(visuals.summary?.storeCheckCount || 0)}
                        icon={<ClipboardCheck />}
                        tone="neutral"
                    />
                </div>

                <ProductPriceExplorer
                    products={filteredProducts}
                    globalTimeline={visuals.timeline || []}
                    selectedProductId={focusedInFilter}
                    onSelectProduct={(product) => this.setFocusedProductId(product.productId)}
                    onReviewObservation={this.reviewObservation}
                    reviewing={reviewingObservation}
                    anomalyThreshold={anomalyThreshold}
                />
            </>
        );
    }

    renderBody(vm) {
        const { dataLoading, priceOptions, selectedType, visuals, loadError } = this.state;

        if (priceOptions.length === 0) {
            return (
                <StateCard>
                    <EmptyState icon={<ListChecks />} description="No hay listas de precio configuradas" />
                </StateCard>
            );
        }
        if (!selectedType) {
            return (
                <StateCard>
                    <EmptyState icon={<ListChecks />} description="Selecciona una lista de precios" />
                </StateCard>
            );
        }
        if (dataLoading) return <TableSkeleton rows={8} columns={4} />;
        if (loadError) {
            return (
                <StateCard>
                    <EmptyState
                        tone="danger"
                        icon={<TriangleAlert />}
                        title={loadError}
                        action={
                            <Button icon={<RotateCw />} onClick={() => this.retryVisuals()}>
                                Reintentar
                            </Button>
                        }
                    />
                </StateCard>
            );
        }
        if (visuals) return this.renderVisuals(vm);
        return (
            <StateCard>
                <EmptyState tone="danger" icon={<TriangleAlert />} description="No se pudo cargar la visualización" />
            </StateCard>
        );
    }

    render() {
        const { loading, accessDenied, error } = this.state;
        const vm = this.getViewModel();

        return (
            <Layout current="distributor-prices">
                {this.props.contextHolder}

                <PageHeader
                    icon={<BadgeDollarSign />}
                    title="Levantamiento Mary"
                    description="Precios de productos individuales capturados en storechecks — compara por piso vs lista oficial."
                />

                {accessDenied ? (
                    <StateCard>
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para distribuidores, asesores, gerentes y administradores."
                        />
                    </StateCard>
                ) : error ? (
                    <StateCard>
                        <EmptyState tone="danger" icon={<TriangleAlert />} title="Error al cargar listas de precio" />
                    </StateCard>
                ) : loading ? (
                    <TableSkeleton rows={6} columns={4} />
                ) : (
                    <>
                        {this.renderFilters(vm)}
                        {this.renderBody(vm)}
                    </>
                )}
            </Layout>
        );
    }
}

export default withNavigate(DistributorPriceVisuals);
